import Head from 'next/head';
import {useRouter} from 'next/router';
import {type FC, useEffect, useState} from 'react';
import {MdArrowBack} from 'react-icons/md';
import {TITLE_SUFFIX} from '../../../config/constants';
import {GoogleAdBannerName} from '../../../config/constants/googleAdBanner';
import {GSSKT_HOST} from '../../../config/constants/hosts';
import {OGP_IMAGE_PATH_DEFAULT} from '../../../config/constants/img';
import {DISCLAIMER, INQUIRY} from '../../../config/constants/routes';
import {isReleasedRedesignRecruitment} from '../../../config/unReleasePage';
import {
  RECRUITMENT_DETAIL_RESOURCE_TYPE,
  RECRUITMENT_SCHEDULE_WARNING_STATUS,
} from '../../../core/entities/recruitment/constants';
import type {
  RecruitmentDetailResource,
  RecruitmentDetailResponse,
} from '../../../core/entities/recruitment/schema';
import {useGetCompanyInfo} from '../../../core/repositories/companiesRepository';
import {GoogleAdBanner} from '../../../shared/components/Banner/GoogleAdBanner';
import {BreadcrumbsComponent} from '../../../shared/components/BreadcrumbsComponent';
import {CommonLink} from '../../../shared/components/CommonLink';
import {useSignUpAppealModal} from '../../../shared/contexts/SignUpAppealModalContext';
import {useGoogleAdBanner} from '../../../shared/services/useGoogleAdBanner';
import {useMediaQuery} from '../../../shared/services/useMediaQuery';
import {FollowCompletionModal} from '../../recruitingInfo/components/Container/Presenter/FollowCompletionModal';
import {useCalendarPromptModal} from '../../recruitingInfo/usecase/useCalendarPromptModal';
import {addStyleToResourceContent} from '../usecases/addStyleToResourceContent';
import {getScheduleWarningStatus} from '../usecases/getScheduleWarningStatus';
import type {RecruitmentDetailResourceWithParsedContent} from '../usecases/parseResourceContent';
import {useCompanyDataDTO} from '../usecases/useCompanyDataDTO';
import {useSignUpAppealOnScroll} from '../usecases/useSignUpAppealOnScroll';
import {ArchiveSignUpAppealModal} from './Container/Presenter/ArchiveSignUpAppealModal';
import {BottomCompanyInfo} from './Container/Presenter/BottomCompanyInfo';
import {CompanyContentList} from './Container/Presenter/CompanyContentList';
import {CompanyContentTab} from './Container/Presenter/CompanyContentListTab';
import {LinksToAdminGsskt} from './Container/Presenter/LinksToAdminGsskt';
import {RecruitmentHeaderPC} from './Container/Presenter/RecruitmentHeaderPC';
import {RecruitmentHeaderSP} from './Container/Presenter/RecruitmentHeaderSP';
import {RecruitmentLeftColumn} from './Container/Presenter/RecruitmentLeftColumn';
import {RecruitmentTitle} from './Container/Presenter/RecruitmentTitle';
import {ResourceContentItem} from './Container/Presenter/ResourceContentItem';
import {ResourceLinkItem} from './Container/Presenter/ResourceLinkItem';
import {ScheduleList} from './Container/Presenter/ScheduleList';
import {ScheduleWarning} from './Container/Presenter/ScheduleWarning';
import {TopCompanyInfo} from './Container/Presenter/TopCompanyInfo';
import {UpdatedBy} from './Container/Presenter/UpdatedAt';
import {RelatedContentList} from './Container/RelatedContentList/RelatedContentList';
import {RecommendedRecruitmentList} from './RecruitmentList/RecommendedRecruitmentList';

type Props = {
  recruitmentItemId: number;
  recruitmentDetail: RecruitmentDetailResponse;
  recruitmentDetailId: number;
  parsedResourceContents: RecruitmentDetailResourceWithParsedContent[];
  decodedResources?: RecruitmentDetailResource[];
  isLogin: boolean;
};

export const ArchivesPage: FC<Props> = ({
  recruitmentItemId,
  recruitmentDetail,
  recruitmentDetailId,
  parsedResourceContents,
  decodedResources,
  isLogin,
}) => {
  const router = useRouter();
  const currentUrl = `${GSSKT_HOST}${decodeURI(router.asPath)}`;
  const {isPC} = useMediaQuery();
  const googleAdBannerNames = [
    GoogleAdBannerName.C_OVER_POST,
    GoogleAdBannerName.C_UNDER_POST,
    GoogleAdBannerName.D_FEED_LEFT_SMALL,
  ];
  useGoogleAdBanner(googleAdBannerNames);

  const pageTitle = `${recruitmentDetail.title}${recruitmentDetail.typeV2.key === 'intern' ? '｜インターンの募集情報詳細' : ''}`;
  const description = recruitmentDetail.resources[0]?.content.slice(0, 79);

  const {
    companyId,
    companyName,
    companyIndustry,
    companyLogoImageUrl,
    companyMainPhotoImageUrl,
    hasReport,
  } = useCompanyDataDTO({recruitmentDetail});

  const [isHydrated, setIsHydrated] = useState(false); // Hydration error防止の為
  const [isResourceContentLoading, setIsResourceContentLoading] =
    useState(true);

  const {isOpened: isSignUpAppealOpened, setIsOpened: setIsSignUpAppealOpened} =
    useSignUpAppealModal();
  useSignUpAppealOnScroll({
    isValid: isLogin,
    setIsOpened: setIsSignUpAppealOpened,
  });

  // 他ページへ遷移してもモーダルの開閉状態が残らないようにする
  useEffect(() => {
    return () => setIsSignUpAppealOpened(false);
  }, [setIsSignUpAppealOpened]);

  const {data: companyInfo, isLoading} = useGetCompanyInfo({companyId});
  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    addStyleToResourceContent()
      .then((): void => {
        setIsResourceContentLoading(false);
      })
      .catch((): void => {
        throw new Error('Failed to add style to DOM');
      });
  }, [isHydrated]);

  const status = getScheduleWarningStatus(
    recruitmentDetail.scheduleWarning,
    recruitmentDetail.displayEndAt,
  );

  const {
    modalVisible,
    suppressChecked,
    setSuppressChecked,
    handleCalendarClose,
    handleFollowSuccess,
  } = useCalendarPromptModal();

  const onFollowSuccess = () => {
    const firstSchedule = recruitmentDetail.schedules[0];
    if (!firstSchedule) {
      return;
    }
    handleFollowSuccess(firstSchedule.hasAddCalendar);
  };

  const crumbs = [
    {label: 'Home', href: '/'},
    {label: '募集を探す', href: '/recruiting_info'},
    {label: recruitmentDetail.title, href: currentUrl},
  ];

  return (
    <>
      <Head>
        <title key="title">{`${pageTitle}${TITLE_SUFFIX}`}</title>
        <link rel="canonical" href={currentUrl} key="canonical" />
        <meta key="og:title" property="og:title" content={pageTitle} />
        <meta key="description" property="description" content={description} />
        <meta
          key="og:description"
          property="og:description"
          content={description}
        />
        <meta key="og:url" property="og:url" content={currentUrl} />
        <meta
          key="og:image"
          property="og:image"
          content={companyMainPhotoImageUrl || OGP_IMAGE_PATH_DEFAULT}
        />
        <meta key="twitter:title" name="twitter:title" content={pageTitle} />
        <meta
          key="twitter:description"
          name="twitter:description"
          content={description}
        />
        <meta
          key="twitter:image"
          name="twitter:image"
          content={companyMainPhotoImageUrl || OGP_IMAGE_PATH_DEFAULT}
        />
      </Head>
      {isReleasedRedesignRecruitment() ? (
        <div className="w-full pc:w-auto">
          <div className="my-8 hidden pc:block">
            <BreadcrumbsComponent crumbs={crumbs} />
          </div>
          <div className="flex w-full justify-center gap-16 px-8 pc:px-0">
            <div className="sticky top-[100px] h-fit hidden pc:block">
              <div>
                <RecruitmentLeftColumn
                  companyId={companyId}
                  companyName={companyName}
                  companyIndustry={companyIndustry}
                  companyLogoImageUrl={''}
                  companyFollowerCount={
                    companyInfo?.ok ? companyInfo.value.followerCount : 0
                  }
                  companyIsFollowed={
                    companyInfo?.ok ? companyInfo.value.followed : false
                  }
                  isLoading={isLoading}
                  hasReport={hasReport}
                  shareTitle={pageTitle}
                  shareUrl={currentUrl}
                  onFollowSuccess={onFollowSuccess}
                />
                <GoogleAdBanner
                  googleAdBannerName={GoogleAdBannerName.D_FEED_LEFT_SMALL}
                />
              </div>
            </div>
            <div className="flex flex-col pc:w-[814px] w-full gap-8">
              <div className="hidden pc:block">
                <RecruitmentHeaderPC
                  title={recruitmentDetail.title}
                  recruitmentType={recruitmentDetail.typeV2}
                  targetYear={recruitmentDetail.targetYear}
                  groupedJobTypes={recruitmentDetail.groupedJobTypes}
                  tags={recruitmentDetail.tags}
                  schedules={recruitmentDetail.schedules}
                  modifiedAt={recruitmentDetail.modifiedAt}
                  recruitmentData={{
                    companyId,
                    companyName,
                    detailURL: currentUrl,
                  }}
                />
              </div>
              <div className="hidden pc:flex bg-background-1 my-8 justify-center h-[60px]">
                <GoogleAdBanner
                  googleAdBannerName={GoogleAdBannerName.C_OVER_POST}
                />
              </div>
              <div className="pc:hidden flex flex-col gap-8">
                <div className="flex bg-white-1 rounded-16 shadow-card p-16">
                  <CommonLink href="/recruiting_info">
                    <MdArrowBack
                      className="bg-background rounded-16"
                      size={24}
                    />
                  </CommonLink>
                  <p className="ml-8 font-bold text-lg">募集を探す</p>
                </div>
                <RecruitmentHeaderSP
                  company={{
                    id: companyId,
                    name: companyName,
                    industry: companyIndustry,
                    logoURL: '',
                    isFollowed: companyInfo?.ok
                      ? companyInfo.value.followed
                      : false,
                    followerCount: companyInfo?.ok
                      ? companyInfo.value.followerCount
                      : 0,
                    isLoading: isLoading,
                  }}
                  hasReport={hasReport}
                  title={recruitmentDetail.title}
                  recruitmentType={recruitmentDetail.typeV2}
                  targetYear={recruitmentDetail.targetYear}
                  groupedJobTypes={recruitmentDetail.groupedJobTypes}
                  tags={recruitmentDetail.tags}
                  schedules={recruitmentDetail.schedules}
                  modifiedAt={recruitmentDetail.modifiedAt}
                  shareTitle={pageTitle}
                  shareUrl={currentUrl}
                  recruitmentData={{
                    companyId,
                    companyName,
                    detailURL: currentUrl,
                  }}
                  onFollowSuccess={onFollowSuccess}
                />
              </div>
              <ScheduleWarning
                companyName={companyName}
                scheduleWarning={recruitmentDetail.scheduleWarning}
                displayEndAt={recruitmentDetail.displayEndAt}
                hasOtherRecruitmentList={false}
              />
              {recruitmentDetail.canEdit && (
                <LinksToAdminGsskt recruitmentItemID={recruitmentItemId} />
              )}
              {parsedResourceContents.map((resource) => {
                return (
                  <ResourceContentItem
                    key={resource.resourceID}
                    resource={resource}
                    isHydrated={true}
                  />
                );
              })}
              <div className="mt-8 bg-white p-16 rounded-16">
                <p className="text-base text-black leading">
                  詳細、ご応募は下記リンクよりご確認ください。
                </p>
                {decodedResources === undefined
                  ? recruitmentDetail.resources.map(
                      (resource) =>
                        resource.resourceType ===
                          RECRUITMENT_DETAIL_RESOURCE_TYPE.LINK && (
                          <ResourceLinkItem
                            resource={resource}
                            key={resource.resourceID}
                          />
                        ),
                    )
                  : decodedResources.map(
                      (resource) =>
                        resource.resourceType ===
                          RECRUITMENT_DETAIL_RESOURCE_TYPE.LINK && (
                          <ResourceLinkItem
                            resource={resource}
                            key={resource.resourceID}
                          />
                        ),
                    )}
              </div>
              {isLogin && (
                <CommonLink
                  href={INQUIRY}
                  className="text-base text-link leading pc:hover:opacity-80"
                >
                  掲載内容の間違いを報告する
                </CommonLink>
              )}
              <div className="mt-16">
                <p className="text-sm text-black leading">
                  弊サイトは情報の正確性に万全を期しておりますが、対象企業による募集情報の急な変更などにより、締切日等について最新の情報を掲載できない場合がございます。
                  <br />
                  大変お手数ですがご志望の企業につきましては、各社の求人情報ページにて最新の情報をご参照いただくようお願い致します。
                  <CommonLink
                    href={DISCLAIMER}
                    className="text-sm text-link underline pc:hover:opacity-80"
                  >
                    こちらの免責事項
                  </CommonLink>
                  もご確認ください。
                </p>
              </div>
              <div className="hidden pc:flex py-16 mt-16  justify-center h-[92px]">
                <GoogleAdBanner
                  googleAdBannerName={GoogleAdBannerName.C_UNDER_POST}
                />
              </div>
              {!isLogin && (
                <ArchiveSignUpAppealModal
                  opened={isSignUpAppealOpened}
                  onClose={() => setIsSignUpAppealOpened(false)}
                  recruitmentItemId={recruitmentItemId}
                />
              )}
              <div className="hidden pc:block">
                <RecommendedRecruitmentList
                  recruitmentDetailId={recruitmentDetailId}
                />
              </div>
            </div>
          </div>
          {recruitmentDetail.schedules[0] && (
            <FollowCompletionModal
              opened={modalVisible}
              onClose={handleCalendarClose}
              recruitmentId={Number(router.query.id) || 0}
              recruitmentTitle={recruitmentDetail.title}
              scheduleId={recruitmentDetail.schedules[0].id}
              isCalendarRegistered={
                recruitmentDetail.schedules[0].hasAddCalendar
              }
              calendarRegisteredCount={
                recruitmentDetail.schedules[0].addCalendarUsers
              }
              companyId={companyId}
              companyName={companyName}
              detailURL={currentUrl}
              pagePath={router.asPath}
              suppressChecked={suppressChecked}
              onSuppressChange={setSuppressChecked}
            />
          )}
        </div>
      ) : (
        <div className="w-full">
          <TopCompanyInfo
            companyId={companyId}
            companyName={companyName}
            companyIndustry={companyIndustry}
          />
          <div className="mt-16 flex">
            {/* 左 */}
            {isPC && (
              <div className="w-[200px]">
                <CompanyContentList
                  companyId={companyId}
                  title={pageTitle}
                  currentUrl={currentUrl}
                  hasReport={hasReport}
                />
                <GoogleAdBanner
                  googleAdBannerName={GoogleAdBannerName.D_FEED_LEFT_SMALL}
                />
              </div>
            )}
            {/* 中央 */}
            <div className="w-full pc:w-[664px] pc:ml-12">
              {!isPC && (
                <CompanyContentTab
                  companyId={companyId}
                  hasReport={hasReport}
                />
              )}
              <RecruitmentTitle
                title={recruitmentDetail.title}
                targetYear={recruitmentDetail.targetYear}
                groupedJobTypes={recruitmentDetail.groupedJobTypes}
                tags={recruitmentDetail.tags}
                recruitmentType={recruitmentDetail.typeV2}
              />
              <div className="w-full bg-white p-16 pc:p-24 my-16">
                {recruitmentDetail.canEdit && (
                  <LinksToAdminGsskt recruitmentItemID={recruitmentItemId} />
                )}
                {isPC && (
                  <div
                    className={'bg-white mb-32 flex justify-center h-[60px]'}
                  >
                    <GoogleAdBanner
                      googleAdBannerName={GoogleAdBannerName.C_OVER_POST}
                    />
                  </div>
                )}
                {status === RECRUITMENT_SCHEDULE_WARNING_STATUS.OK &&
                  recruitmentDetail.schedules.length > 0 && (
                    <ScheduleList schedules={recruitmentDetail.schedules} />
                  )}
                <ScheduleWarning
                  companyName={companyName}
                  scheduleWarning={recruitmentDetail.scheduleWarning}
                  displayEndAt={recruitmentDetail.displayEndAt}
                  hasOtherRecruitmentList={false}
                />
                <div
                  className={`mt-16 ${isResourceContentLoading ? 'hidden' : ''}`}
                >
                  {parsedResourceContents.map((resource) => (
                    <ResourceContentItem
                      key={resource.resourceID}
                      resource={resource}
                      isHydrated={isHydrated}
                    />
                  ))}
                </div>

                <div className="mt-16">
                  <p className="text-base text-black leading">
                    詳細、ご応募は下記リンクよりご確認ください。
                  </p>
                  {decodedResources === undefined
                    ? recruitmentDetail.resources.map(
                        (resource) =>
                          resource.resourceType ===
                            RECRUITMENT_DETAIL_RESOURCE_TYPE.LINK && (
                            <ResourceLinkItem
                              resource={resource}
                              key={resource.resourceID}
                            />
                          ),
                      )
                    : decodedResources.map(
                        (resource) =>
                          resource.resourceType ===
                            RECRUITMENT_DETAIL_RESOURCE_TYPE.LINK && (
                            <ResourceLinkItem
                              resource={resource}
                              key={resource.resourceID}
                            />
                          ),
                      )}
                </div>
                {isLogin && (
                  <div className="mt-16">
                    <CommonLink
                      href={INQUIRY}
                      className="text-base text-link leading pc:hover:opacity-80"
                    >
                      掲載内容の間違いを報告する
                    </CommonLink>
                  </div>
                )}
                <UpdatedBy updatedAt={recruitmentDetail.modifiedAt} />
                <BottomCompanyInfo
                  companyId={companyId}
                  companyName={companyName}
                  logoImageUrl={companyLogoImageUrl}
                  industry={companyIndustry}
                  isArchivesPage={true}
                  isPC={isPC}
                />
                {isPC && (
                  <div className="bg-white py-16 mt-16 flex justify-center h-[92px]">
                    <GoogleAdBanner
                      googleAdBannerName={GoogleAdBannerName.C_UNDER_POST}
                    />
                  </div>
                )}
                <div className="mt-16">
                  <p className="text-sm text-black leading">
                    弊サイトは情報の正確性に万全を期しておりますが、対象企業による募集情報の急な変更などにより、締切日等について最新の情報を掲載できない場合がございます。
                    <br />
                    大変お手数ですがご志望の企業につきましては、各社の求人情報ページにて最新の情報をご参照いただくようお願い致します。
                    <CommonLink
                      href={DISCLAIMER}
                      className="text-sm text-link underline pc:hover:opacity-80"
                    >
                      こちらの免責事項
                    </CommonLink>
                    もご確認ください。
                  </p>
                </div>
              </div>
              {isPC && (
                <RecommendedRecruitmentList
                  recruitmentDetailId={recruitmentDetailId}
                />
              )}
              {isPC && isLogin && (
                <RelatedContentList recruitmentItemId={recruitmentItemId} />
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
